/**
 * Hybrid Renderer - Orchestrates Canvas and SVG rendering
 * Manages coordination between Canvas (static) and SVG (dynamic) layers
 */

import * as joint from '@joint/core'
import { CanvasRenderer, CanvasElement, convertToCanvasElement } from './canvasRenderer'
import { log, LogCategory } from '../../../map-editor-shared/lib/logger'

export interface HybridRendererOptions {
  canvasWidth?: number
  canvasHeight?: number
  staticTypes?: string[]  // Element types to render on Canvas
  dynamicTypes?: string[]  // Element types to render on SVG
}

export class HybridRenderer {
  private canvasRenderer: CanvasRenderer
  private paper: joint.dia.Paper | null = null
  private staticTypes: Set<string>
  private isInitialized = false
  private canvasElements: Map<string, CanvasElement> = new Map()
  private svgElements: Map<string, joint.dia.Element> = new Map()
  private transformSyncEnabled = true
  private canvasClickHandler: ((event: PointerEvent) => void) | null = null
  private canvasHoverHandler: ((event: PointerEvent) => void) | null = null

  constructor(options: HybridRendererOptions = {}) {
    const {
      canvasWidth = 10000,  // Reduced from 100000 for better pan performance
      canvasHeight = 10000, // 10000x10000 = 100M pixels (vs 10B pixels before)
      staticTypes = ['parking_space', 'background', 'wall', 'pillar'],
    } = options

    this.canvasRenderer = new CanvasRenderer(canvasWidth, canvasHeight)
    this.staticTypes = new Set(staticTypes)

  }

  /**
   * Initialize with JointJS paper
   */
  async initialize(paper: joint.dia.Paper, container: HTMLElement): Promise<void> {
    if (this.isInitialized) {
      return
    }

    this.paper = paper

    // Use SVG's actual parent as the container, or fall back to provided container
    const svg = paper.el as HTMLElement
    const actualContainer = svg.parentElement || container

    // Ensure container has relative positioning for absolute children
    actualContainer.style.position = 'relative'

    // Insert canvas before SVG (z-index layering)
    const canvas = this.canvasRenderer.getCanvas()

    // Check if SVG is in the actual container
    if (!actualContainer.contains(svg)) {
      actualContainer.appendChild(svg)
    }
    actualContainer.insertBefore(canvas, svg)

    // Set up transform syncing
    this.setupTransformSync()

    // Add event handlers to canvas for click/hover detection
    this.setupCanvasEventHandlers()

    this.isInitialized = true
  }

  /**
   * Setup transform synchronization between Paper and Canvas
   */
  private setupTransformSync(): void {
    if (!this.paper || !this.transformSyncEnabled) return

    // Sync initial transform
    this.syncTransform()

    // Listen to paper transform changes using event delegation
    const paperElement = this.paper.el as HTMLElement
    const observer = new MutationObserver(() => {
      if (this.transformSyncEnabled) {
        this.syncTransform()
      }
    })

    observer.observe(paperElement, {
      attributes: true,
      attributeFilter: ['transform'],
      subtree: false
    })

    // Also sync on paper scale/translate events
    this.paper.on('scale', () => this.syncTransform())
    this.paper.on('translate', () => this.syncTransform())
  }

  /**
   * Setup canvas event handlers for interactivity
   */
  private setupCanvasEventHandlers(): void {
    const canvas = this.canvasRenderer.getCanvas()
    if (!this.paper || !canvas) return

    // Click handler
    this.canvasClickHandler = (evt: PointerEvent) => {
      if (!this.paper) return

      const rect = canvas.getBoundingClientRect()
      const x = evt.clientX - rect.left
      const y = evt.clientY - rect.top

      // Transform to paper coordinates
      const paperPoint = this.paper.clientToLocalPoint({ x: evt.clientX, y: evt.clientY })

      // Find element at position using hitTest
      const element = this.canvasRenderer.hitTest(x, y)
      if (element) {
        // Trigger paper event for consistency
        this.paper.trigger('cell:pointerclick', {
          model: { id: element.id, get: (key: string) => (element as any).data?.[key] },
          x: paperPoint.x,
          y: paperPoint.y
        })
      }
    }

    // Hover handler for cursor feedback
    this.canvasHoverHandler = (evt: PointerEvent) => {
      const rect = canvas.getBoundingClientRect()
      const x = evt.clientX - rect.left
      const y = evt.clientY - rect.top

      const element = this.canvasRenderer.hitTest(x, y)
      canvas.style.cursor = element ? 'pointer' : 'default'
    }

    canvas.addEventListener('pointerdown', this.canvasClickHandler)
    canvas.addEventListener('pointermove', this.canvasHoverHandler)
  }

  /**
   * Sync Canvas transform with JointJS paper
   */
  private syncTransform(): void {
    if (!this.paper) return

    // Get paper transform matrix
    const matrix = this.paper.matrix()

    // Apply to Canvas renderer using applyTransform
    const scale = matrix.a  // Assuming uniform scale (a === d)
    this.canvasRenderer.applyTransform(
      scale,
      matrix.e,  // translateX
      matrix.f   // translateY
    )
  }

  /**
   * Clear all elements
   */
  clearElements(): void {
    // Clear canvas elements
    this.canvasRenderer.clearElements()
    this.canvasElements.clear()

    // Clear SVG elements from paper
    if (this.paper) {
      this.svgElements.forEach(element => {
        element.remove()
      })
    }
    this.svgElements.clear()
  }

  /**
   * Determine if element should render on Canvas
   */
  private shouldRenderOnCanvas(element: joint.dia.Element): boolean {
    const type = element.get('data')?.type || element.get('type')
    const layer = element.get('layer') || element.get('data')?.layer || ''

    // Keep point-like symbols (CCTV/emergency/light markers) on SVG layer.
    if (type === 'point') {
      return false
    }

    // Check explicit types
    if (type && this.staticTypes.has(type)) {
      return true
    }

    // Check layer-based rules
    const staticLayers = ['parking', 'wall', 'background', 'pillar', 'zone']
    return staticLayers.some(staticLayer => layer.toLowerCase().includes(staticLayer))
  }

  /**
   * Process element for hybrid rendering
   */
  private processElement(element: joint.dia.Element): void {
    if (!this.paper) return

    const elementId = element.id as string

    // Skip if element type is not recognized for Canvas
    if (this.shouldRenderOnCanvas(element)) {
      // Convert to Canvas element
      const canvasElement = convertToCanvasElement(element)
      if (canvasElement) {
        this.canvasElements.set(elementId, canvasElement)
        // Remove from SVG if it exists
        if (this.svgElements.has(elementId)) {
          element.remove()
          this.svgElements.delete(elementId)
        }
      }
    } else {
      // Keep in SVG
      this.svgElements.set(elementId, element)
      // Remove from Canvas if it exists
      if (this.canvasElements.has(elementId)) {
        this.canvasElements.delete(elementId)
      }
    }
  }

  /**
   * Process elements and distribute between Canvas and SVG
   */
  processElements(elements: joint.dia.Element[]): void {
    if (!this.paper || !this.isInitialized) {
      return
    }

    const startTime = performance.now()

    // Clear existing elements
    this.clearElements()

    // Process each element
    elements.forEach(element => {
      this.processElement(element)
    })

    // Convert canvas elements map to array
    const canvasElements = Array.from(this.canvasElements.values())

    // Always use Canvas 2D

    // Hide any potential canvas to prevent flicker
    const canvas = this.canvasRenderer.getCanvas()
    canvas.style.display = 'none'

    // Clear and add elements to canvas
    this.canvasRenderer.clearElements()
    this.canvasRenderer.addElements(canvasElements)

    // Show canvas
    canvas.style.display = 'block'

    // Keep dynamic elements in SVG

    const duration = performance.now() - startTime
  }

  /**
   * Add single element
   */
  addElement(element: joint.dia.Element): void {
    if (!this.paper || !this.isInitialized) return

    this.processElement(element)

    // Update renderers
    const elementId = element.id as string
    if (this.canvasElements.has(elementId)) {
      const canvasElements = Array.from(this.canvasElements.values())
      this.canvasRenderer.clearElements()
      this.canvasRenderer.addElements(canvasElements)
    }
  }

  /**
   * Remove element
   */
  removeElement(elementId: string): void {
    // Remove from Canvas
    if (this.canvasElements.has(elementId)) {
      this.canvasElements.delete(elementId)
      const canvasElements = Array.from(this.canvasElements.values())
      this.canvasRenderer.clearElements()
      this.canvasRenderer.addElements(canvasElements)
    }

    // Remove from SVG
    if (this.svgElements.has(elementId)) {
      const element = this.svgElements.get(elementId)
      if (element) {
        element.remove()
      }
      this.svgElements.delete(elementId)
    }
  }

  /**
   * Clear all rendered elements
   */
  clear(): void {
    this.canvasRenderer.clearElements()
    this.canvasElements.clear()
    this.svgElements.clear()
  }

  /**
   * Get renderer statistics
   */
  getStats() {
    return {
      canvas: {
        elements: this.canvasElements.size
      },
      svg: {
        elements: this.svgElements.size
      },
      total: this.canvasElements.size + this.svgElements.size
    }
  }

  /**
   * Get canvas-rendered elements for fallback hit-testing.
   * Returns elements with position and bounds for bbox checks.
   */
  getCanvasElements(): ReadonlyMap<string, CanvasElement> {
    return this.canvasElements
  }

  /**
   * Hit-test from client (screen) coordinates.
   * Returns the element ID if a Canvas-rendered element was hit, null otherwise.
   */
  hitTestFromClient(clientX: number, clientY: number): string | null {
    const canvas = this.canvasRenderer.getCanvas()
    const rect = canvas.getBoundingClientRect()
    const x = clientX - rect.left
    const y = clientY - rect.top
    const element = this.canvasRenderer.hitTest(x, y)
    return element ? element.id : null
  }

  /**
   * Check if renderer is using Canvas for a specific element
   */
  isUsingCanvas(elementId: string): boolean {
    return this.canvasElements.has(elementId)
  }

  /**
   * Force redraw
   */
  redraw(): void {
    // Sync transform first
    this.syncTransform()

    // Redraw canvas elements
    const canvasElements = Array.from(this.canvasElements.values())
    this.canvasRenderer.clearElements()
    this.canvasRenderer.addElements(canvasElements)

    // SVG elements are automatically redrawn by JointJS
    if (this.paper && this.paper.options.width && this.paper.options.height) {
      // Force paper to update dimensions if needed
      this.paper.setDimensions(this.paper.options.width, this.paper.options.height)
    }
  }

  /**
   * Destroy and clean up
   */
  destroy(): void {
    try {
      // Remove event listeners to prevent memory leaks
      const canvas = this.canvasRenderer.getCanvas()
      if (this.canvasClickHandler) {
        canvas.removeEventListener('pointerdown', this.canvasClickHandler)
        this.canvasClickHandler = null
      }
      if (this.canvasHoverHandler) {
        canvas.removeEventListener('pointermove', this.canvasHoverHandler)
        this.canvasHoverHandler = null
      }

      // Safely destroy canvas renderer
      try {
        this.canvasRenderer.destroy()
      } catch (e) {
      }

      this.canvasElements.clear()
      this.svgElements.clear()
      this.paper = null
      this.isInitialized = false
    } catch (error) {
      // Ensure cleanup even if there's an error
      this.paper = null
      this.isInitialized = false
    }
  }
}